import axios from 'axios'
import type { Agent, AgentPermission, AgentPermissionsInfo, Visibility, PermissionLevel } from '../types/agent'
import type { TeamTemplate, TeamRole, TeamInstance, TeamAgentAssignment } from '../types/team'

const API_BASE_URL = '/api'

// Configuration d'axios avec intercepteur pour l'authentification
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use((config) => {
  const authStorage = localStorage.getItem('auth-storage')
  if (authStorage) {
    try {
      const authData = JSON.parse(authStorage)
      // * Zustand persist stocke les données dans state
      const tokenValue = authData.state?.token
      if (tokenValue) {
        config.headers.Authorization = `Bearer ${tokenValue}`
      } else {
        console.warn('Token non trouvé dans auth-storage')
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du token:', error)
    }
  } else {
    console.warn('auth-storage non trouvé dans localStorage')
  }
  return config
})

// Intercepteur de réponse pour gérer les erreurs 401 (non authentifié)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // * Token invalide ou expiré
      const currentPath = window.location.pathname
      if (currentPath !== '/login' && currentPath !== '/auth/callback') {
        console.warn('Session expirée, redirection vers la page de login')
        // * Nettoyer le localStorage et rediriger
        localStorage.removeItem('auth-storage')
        // * Utiliser window.location.href pour forcer un rechargement complet
        window.location.href = '/login?error=session_expired'
      }
    }
    return Promise.reject(error)
  }
)

// * Types are now imported from '../types/agent'
// * Re-export for backward compatibility
export type { Agent, AgentPermission, AgentPermissionsInfo, Visibility, PermissionLevel } from '../types/agent'

// * Team types are now imported from '../types/team'

export const agentService = {
  getAllAgents: async (): Promise<Agent[]> => {
    const response = await apiClient.get('/agents')
    return response.data.data
  },

  getAgentById: async (id: string): Promise<Agent> => {
    const response = await apiClient.get(`/agents/${id}`)
    return response.data.data
  },

  createAgent: async (agentData: Partial<Agent>): Promise<Agent> => {
    const response = await apiClient.post('/agents', agentData)
    return response.data.data
  },

  updateAgent: async (id: string, agentData: Partial<Agent>): Promise<Agent> => {
    const response = await apiClient.put(`/agents/${id}`, agentData)
    return response.data.data
  },

  deleteAgent: async (id: string): Promise<void> => {
    await apiClient.delete(`/agents/${id}`)
  },

  deployAgent: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/agents/${id}/deploy`)
    return response.data.data
  },

  stopAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/stop`)
  },

  // * Hibernate agent (stop container without removing)
  hibernateAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/hibernate`)
  },

  // * Resume agent (start container)
  resumeAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/resume`)
  },

  // * Delete agent instance (unregister and remove container)
  deleteAgentInstance: async (id: string): Promise<void> => {
    await apiClient.delete(`/agents/${id}/instance`)
  },

  // * Get agent metrics
  getAgentMetrics: async (agentIds: string[], startTime: Date, endTime: Date): Promise<any[]> => {
    const response = await apiClient.get('/agents/metrics', {
      params: {
        agentIds: agentIds.join(','),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      }
    })
    return response.data.data
  },

  getAgentStatus: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/agents/${id}/status`)
    return response.data.data
  },

  getAgentLogs: async (id: string, lines: number = 100): Promise<string[]> => {
    const response = await apiClient.get(`/agents/${id}/logs?lines=${lines}`)
    return response.data.data
  },

  duplicateAgent: async (id: string, isFork: boolean = false): Promise<Agent> => {
    const response = await apiClient.post(`/agents/${id}/duplicate?fork=${isFork}`)
    return response.data.data
  },

  getActiveAgents: async (): Promise<Agent[]> => {
    const response = await apiClient.get('/agents/active/list')
    return response.data.data
  },

  cleanInactiveAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/inactive')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanDuplicateAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/duplicates')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgentInstances: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/instances')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgentModels: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/models')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/all')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  // * Deploy agent instance directly
  deployAgentInstance: async (agentId: string, options: { engine?: string; coreUrl?: string; apiKeyIds?: Record<string, string>; teamInstanceId?: string; roleId?: string }): Promise<{ instanceId: string }> => {
    const response = await apiClient.post(`/agents/${agentId}/deploy-instance`, options)
    return response.data.data
  },

  // * Download agent deployment package
  downloadAgentDeploymentPackage: async (agentId: string, options: { engine: string; coreUrl?: string; apiKeyIds?: Record<string, string> }): Promise<Blob> => {
    const response = await apiClient.post(
      `/agents/${agentId}/download-package`,
      options,
      { responseType: 'blob' }
    )
    return response.data
  },
}

export const teamService = {
  // * Team Templates
  getAllTeamTemplates: async (): Promise<TeamTemplate[]> => {
    const response = await apiClient.get('/teams/templates')
    return response.data.data
  },

  getTeamTemplateById: async (id: string): Promise<TeamTemplate> => {
    const response = await apiClient.get(`/teams/templates/${id}`)
    return response.data.data
  },

  createTeamTemplate: async (teamData: { name: string; description?: string; config?: any }): Promise<TeamTemplate> => {
    const response = await apiClient.post('/teams/templates', teamData)
    return response.data.data
  },

  updateTeamTemplate: async (id: string, teamData: Partial<TeamTemplate>): Promise<TeamTemplate> => {
    const response = await apiClient.put(`/teams/templates/${id}`, teamData)
    return response.data.data
  },

  deleteTeamTemplate: async (id: string): Promise<void> => {
    await apiClient.delete(`/teams/templates/${id}`)
  },

  duplicateTeamTemplate: async (id: string): Promise<TeamTemplate> => {
    const response = await apiClient.post(`/teams/templates/${id}/duplicate`)
    return response.data.data
  },

  // * Team Roles
  addTeamRole: async (teamTemplateId: string, roleData: { name: string; description?: string; isUnique: boolean; agentType: 'ai' | 'human' | 'both' }): Promise<TeamRole> => {
    const response = await apiClient.post(`/teams/templates/${teamTemplateId}/roles`, roleData)
    return response.data.data
  },

  updateTeamRole: async (teamTemplateId: string, roleId: string, roleData: Partial<TeamRole>): Promise<TeamRole> => {
    const response = await apiClient.put(`/teams/templates/${teamTemplateId}/roles/${roleId}`, roleData)
    return response.data.data
  },

  deleteTeamRole: async (teamTemplateId: string, roleId: string): Promise<void> => {
    await apiClient.delete(`/teams/templates/${teamTemplateId}/roles/${roleId}`)
  },

  // * Team Instances
  getAllTeamInstances: async (): Promise<TeamInstance[]> => {
    const response = await apiClient.get('/teams/instances')
    return response.data.data
  },

  getTeamInstanceById: async (id: string): Promise<TeamInstance> => {
    const response = await apiClient.get(`/teams/instances/${id}`)
    return response.data.data
  },

  createTeamInstance: async (teamTemplateId: string, name?: string): Promise<TeamInstance> => {
    const response = await apiClient.post('/teams/instances', { teamTemplateId, name })
    return response.data.data
  },

  updateTeamInstance: async (id: string, instanceData: { name?: string }): Promise<TeamInstance> => {
    const response = await apiClient.put(`/teams/instances/${id}`, instanceData)
    return response.data.data
  },

  deleteTeamInstance: async (id: string, reassignments?: { agentId: string; teamInstanceId?: string; roleId?: string }[]): Promise<void> => {
    await apiClient.delete(`/teams/instances/${id}`, { data: { reassignments } })
  },

  duplicateTeamInstance: async (id: string): Promise<TeamInstance> => {
    const response = await apiClient.post(`/teams/instances/${id}/duplicate`)
    return response.data.data
  },

  // * Agent Team Assignments
  assignAgentToTeam: async (agentId: string, teamInstanceId: string, roleId: string): Promise<TeamAgentAssignment> => {
    const response = await apiClient.post('/teams/assignments', { agentId, teamInstanceId, roleId })
    return response.data.data
  },

  removeAgentFromTeam: async (agentId: string, teamInstanceId: string): Promise<void> => {
    await apiClient.delete(`/teams/assignments`, { data: { agentId, teamInstanceId } })
  },
}

export const engineService = {
  listEngines: async (): Promise<Array<{ name: string; filename: string; path: string }>> => {
    const response = await apiClient.get('/engines/list')
    return response.data.data
  },
}

export const settingsService = {
  getSetting: async (key: string): Promise<string | null> => {
    const response = await apiClient.get(`/settings/${key}`)
    return response.data.data?.value || null
  },

  setSetting: async (key: string, value: string, description?: string): Promise<void> => {
    await apiClient.put(`/settings/${key}`, { value, description })
  },

  getAllSettings: async (): Promise<Record<string, string>> => {
    const response = await apiClient.get('/settings')
    return response.data.data || {}
  },
}

export const permissionService = {
  getAgentPermissions: async (agentId: string): Promise<AgentPermissionsInfo> => {
    const response = await apiClient.get(`/agents/${agentId}/permissions`)
    return response.data.data
  },

  setVisibility: async (agentId: string, visibility: Visibility): Promise<void> => {
    await apiClient.put(`/agents/${agentId}/visibility`, { visibility })
  },

  setPermission: async (agentId: string, userId: string, permissionLevel: PermissionLevel): Promise<void> => {
    await apiClient.post(`/agents/${agentId}/permissions`, { userId, permissionLevel })
  },

  removePermission: async (agentId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/agents/${agentId}/permissions/${userId}`)
  },

  transferOwnership: async (agentId: string, newOwnerId: string): Promise<void> => {
    await apiClient.post(`/agents/${agentId}/transfer-ownership`, { newOwnerId })
  },

  searchUsers: async (searchTerm: string, limit: number = 10): Promise<any[]> => {
    const response = await apiClient.get(`/users/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}`)
    return response.data.data
  },

  getDiscoverableAgents: async (searchTerm?: string): Promise<Agent[]> => {
    const url = searchTerm 
      ? `/agents/discover?q=${encodeURIComponent(searchTerm)}`
      : '/agents/discover'
    const response = await apiClient.get(url)
    return response.data.data
  },
}

export const authService = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password })
    return response.data
  },

  register: async (userData: { username: string; email: string; password: string; name: string }) => {
    const response = await apiClient.post('/auth/register', userData)
    return response.data
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me')
    return response.data
  },

  getGoogleAuthUrl: async () => {
    const response = await apiClient.get('/auth/google/url')
    return response.data
  },
}

export interface User {
  id: string
  email: string
  username: string
  name: string
  avatar?: string
  role: 'guest' | 'user' | 'admin'
  provider: 'local' | 'google'
  createdAt: Date
  updatedAt: Date
}

export const userService = {
  getAllUsers: async (): Promise<User[]> => {
    const response = await apiClient.get('/users')
    return response.data.data
  },

  updateUserRole: async (userId: string, role: 'guest' | 'user' | 'admin'): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}/role`, { role })
    return response.data.data
  },
}

export const notificationService = {
  getNotifications: async (): Promise<{ notifications: any[] }> => {
    const response = await apiClient.get('/notifications')
    return response.data
  },

  markAsRead: async (id: string): Promise<void> => {
    await apiClient.post(`/notifications/${id}/read`)
  },

  markAllAsRead: async (): Promise<void> => {
    await apiClient.post('/notifications/read-all')
  },

  deleteNotification: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/${id}`)
  },

  // * API Keys management
  getApiKeys: async (): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get('/api-keys')
    return response.data.data
  },

  createApiKey: async (input: CreateApiKeyInput): Promise<ApiKey> => {
    const response = await apiClient.post('/api-keys', input)
    return response.data.data
  },

  getApiKeyById: async (id: string): Promise<ApiKey & { decryptedKey: string }> => {
    const response = await apiClient.get(`/api-keys/${id}`)
    return response.data.data
  },

  deleteApiKey: async (id: string): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`)
  },

  getApiKeyByProvider: async (provider: string): Promise<{ provider: string; key: string }> => {
    const response = await apiClient.get(`/api-keys/provider/${provider}`)
    return response.data.data
  },
}

export const apiService = {
  getApiKeys: async (): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get('/api-keys')
    return response.data.data
  },

  createApiKey: async (input: CreateApiKeyInput): Promise<ApiKey> => {
    const response = await apiClient.post('/api-keys', input)
    return response.data.data
  },

  getApiKeyById: async (id: string): Promise<ApiKey & { decryptedKey: string }> => {
    const response = await apiClient.get(`/api-keys/${id}`)
    return response.data.data
  },

  deleteApiKey: async (id: string): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`)
  },

  getApiKeysByProvider: async (provider: string): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get(`/api-keys/provider/${provider}`)
    return response.data.data
  },
}

// * Types for API Keys
export interface ApiKey {
  id: string
  userId: string
  keyType: string
  provider: string
  encryptedKey: string
  keyPreview: string
  description?: string
  createdAt: Date
  updatedAt: Date
}

export interface ApiKeyListItem {
  id: string
  keyType: string
  provider: string
  keyPreview: string
  description?: string
  createdAt: Date
}

export interface CreateApiKeyInput {
  keyType: string
  provider: string
  key: string
  description?: string
}